import React, { useEffect } from 'react';
import { Chip, IconButton } from '@mui/material';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import * as actions from 'features/viewing/redux/actions';
import { useDispatch } from 'react-redux';
import { usePrevious } from 'utils/hooks';

type ChipLabelProps = {
  label: string;
  searchFunctions: {
    search: (val: string) => void;
    searchNext?: () => void;
    searchPrev?: () => void;
    highlightLocation: (location: any) => void;
  };
};

const ChipLabel = ({ label, searchFunctions }: ChipLabelProps) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'row' }}>
      <IconButton
        size="small"
        onClick={() => {
          if (searchFunctions.searchPrev) searchFunctions.searchPrev();
        }}
      >
        <ArrowLeftIcon />
      </IconButton>
      <OverlayTrigger
        placement="top"
        delay={{ show: 500, hide: 400 }}
        trigger={label && label.length > 13 ? ['hover', 'focus'] : undefined}
        overlay={
          <Popover style={{ pointerEvents: 'none' }} id={'chip-label-popover'}>
            <Popover.Content>{label}</Popover.Content>
          </Popover>
        }
      >
        <div
          style={{
            maxWidth: '8rem',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            margin: 'auto',
          }}
        >
          {label}
        </div>
      </OverlayTrigger>
      <IconButton
        size="small"
        onClick={() => searchFunctions.searchNext && searchFunctions.searchNext()}
      >
        <ArrowRightIcon />
      </IconButton>
    </div>
  );
};

type DocumentSearchProps = {
  searchFunctions: {
    search: (val: string) => void;
    searchNext?: () => void;
    searchPrev?: () => void;
    highlightLocation: (location: any) => void;
  };
  docSearchVal: string;
  docSearchLocation: { pageNumber: string; [key: string]: any } | null;
  isAiTools?: boolean;
  sideSummaryOpen?: boolean;
  isSearchMode: boolean;
};

const DocumentSearch = ({
  searchFunctions,
  docSearchVal,
  docSearchLocation,
  isAiTools = false,
  sideSummaryOpen = false,
  isSearchMode = false,
}: DocumentSearchProps) => {
  const dispatch = useDispatch();
  const prevDocSearchVal = usePrevious(docSearchVal);
  const prevDocSearchLocation = usePrevious(docSearchLocation);

  // Helper function to check if two locations are the same
  const isSameLocation = (loc1: any, loc2: any) => {
    if (!loc1 || !loc2) return false;
    return (
      loc1.pageNumber === loc2.pageNumber &&
      loc1.startIndex === loc2.startIndex &&
      loc1.textLength === loc2.textLength &&
      loc1.x === loc2.x &&
      loc1.y === loc2.y
    );
  };

  useEffect(() => {
    if (!searchFunctions) return;
    if (docSearchLocation) {
      if (searchFunctions.highlightLocation) {
        // Preserve explicit showMarkButton if provided by the caller; otherwise infer from context
        const urlParams = new URLSearchParams(window.location.search);
        const hasAskAIParam = !!urlParams.get('askAI');
        const enhancedLocation = {
          ...docSearchLocation,
          showMarkButton:
            (docSearchLocation as any)?.showMarkButton !== undefined
              ? (docSearchLocation as any).showMarkButton
              : isAiTools || sideSummaryOpen || hasAskAIParam,
        };
        searchFunctions.highlightLocation(enhancedLocation);
      } else {
        console.error('DocumentSearch: highlightLocation function not available');
      }
    } else if (docSearchVal && docSearchVal !== prevDocSearchVal) {
      setTimeout(() => searchFunctions.search && searchFunctions.search(docSearchVal));
    }
  }, [
    docSearchVal,
    searchFunctions,
    prevDocSearchVal,
    docSearchLocation,
    dispatch,
    isAiTools,
    sideSummaryOpen,
  ]);

  return docSearchVal && !isAiTools && !sideSummaryOpen && isSearchMode ? (
    <Chip
      label={<ChipLabel label={docSearchVal} searchFunctions={searchFunctions} />}
      size="small"
      sx={{
        fontSize: '1rem',
        mr: '0.5rem',
        cursor: 'initial !important',
      }}
      onDelete={() => {
        if (searchFunctions.search) searchFunctions.search('');
        dispatch(actions.setDocSearch(''));
      }}
    />
  ) : null;
};

export default DocumentSearch;
